import type { DocumentValidator, ValidationResult } from '../validators/document.validator';
import {
  FileDocumentValidator,
  TextDocumentValidator,
  UrlDocumentValidator,
} from '../validators/document.validator';

export interface DocumentUploadHandler {
  validate(): ValidationResult;
  convertToFile(): File | null;
  canHandle(): boolean;
}

export class FileUploadHandler implements DocumentUploadHandler {
  private file: File | null;

  constructor(file: File | null) {
    this.file = file;
  }

  validate(): ValidationResult {
    const validator: DocumentValidator = new FileDocumentValidator(this.file);
    return validator.validate();
  }

  convertToFile(): File | null {
    return this.file;
  }

  canHandle(): boolean {
    return this.file !== null;
  }
}

export class UrlUploadHandler implements DocumentUploadHandler {
  private url: string;

  constructor(url: string) {
    this.url = url;
  }

  validate(): ValidationResult {
    const validator: DocumentValidator = new UrlDocumentValidator(this.url);
    const result = validator.validate();

    if (result.isValid) {
      // URL upload is not yet supported
      return {
        isValid: false,
        error: 'URL upload is not yet supported. Please use file or text upload instead.',
      };
    }

    return result;
  }

  convertToFile(): File | null {
    // URL upload not yet implemented
    return null;
  }

  canHandle(): boolean {
    return this.url.trim().length > 0;
  }
}

export class TextUploadHandler implements DocumentUploadHandler {
  private name: string;
  private content: string;

  constructor(name: string, content: string) {
    this.name = name;
    this.content = content;
  }

  validate(): ValidationResult {
    const validator: DocumentValidator = new TextDocumentValidator(this.name, this.content);
    return validator.validate();
  }

  convertToFile(): File | null {
    if (!this.name.trim() || !this.content.trim()) {
      return null;
    }

    const blob = new Blob([this.content], { type: 'text/plain' });
    return new File([blob], `${this.name}.txt`, { type: 'text/plain' });
  }

  canHandle(): boolean {
    return this.name.trim().length > 0 && this.content.trim().length > 0;
  }
}

export class DocumentUploadHandlerFactory {
  static create(
    type: 'file' | 'url' | 'text',
    options: {
      file?: File | null;
      url?: string;
      textName?: string;
      textContent?: string;
    }
  ): DocumentUploadHandler {
    switch (type) {
      case 'file':
        return new FileUploadHandler(options.file || null);
      case 'url':
        return new UrlUploadHandler(options.url || '');
      case 'text':
        return new TextUploadHandler(options.textName || '', options.textContent || '');
    }
  }
}
